/* eslint-disable no-underscore-dangle */
import { PathLocationStrategy, PlatformLocation } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { AppRequestContextService } from './app-request-context.service';

@Injectable()
export class AppPathLocationStrategy extends PathLocationStrategy {
  private platformLocation = inject(PlatformLocation);
  private appRequestContextService = inject(AppRequestContextService);

  getBaseHref() {
    // If source is path, then we prepend the slug to base path
    if (this.appRequestContextService.source() === 'path') {
      return this.appRequestContextService.slug();
    }

    // Otherwise, base path should be empty
    return '';
  }

  prepareExternalUrl(internal: string): string {
    if (this.appRequestContextService.source() === 'path') {
      return this.joinWithSlash(this.appRequestContextService.slug(), internal);
    }
    return internal;
  }

  path(includeHash: boolean = false): string {
    const slugPrefixUrlPath = `/${this.appRequestContextService.slug()}`;

    let pathname =
      this.platformLocation.pathname + this.normalizeQueryParams(this.platformLocation.search);

    if (pathname.startsWith(slugPrefixUrlPath)) {
      pathname = pathname.substr(slugPrefixUrlPath.length);
    }

    const hash = this.platformLocation.hash;
    return hash && includeHash ? `${pathname}${hash}` : pathname;
  }

  pushState(state: any, title: string, url: string, queryParams: string) {
    const externalUrl = this.prepareExternalUrl(url + this.normalizeQueryParams(queryParams));
    this.platformLocation.pushState(state, title, externalUrl);
  }

  replaceState(state: any, title: string, url: string, queryParams: string) {
    const externalUrl = this.prepareExternalUrl(url + this.normalizeQueryParams(queryParams));
    this.platformLocation.replaceState(state, title, externalUrl);
  }

  private joinWithSlash(start: string, end: string): string {
    if (!start) {
      return end;
    }
    if (!end) {
      return start;
    }
    if (end.startsWith('?')) {
      return start + end;
    }
    if (end.startsWith('/?')) {
      return start + end.substring(1);
    }
    let slashes = 0;
    if (start.endsWith('/')) {
      slashes++;
    }
    if (end.startsWith('/')) {
      slashes++;
    }

    let result: string;
    if (slashes === 2) {
      result = start + end.substring(1);
    } else if (slashes === 1) {
      result = start + end;
    } else {
      result = start + '/' + end;
    }

    // Remove trailing slash unless it's the root path
    if (result.length > 1 && result.endsWith('/')) {
      result = result.slice(0, -1);
    }

    return result;
  }

  private normalizeQueryParams(params: string): string {
    return params && params[0] !== '?' ? '?' + params : params;
  }
}
